<template>
    <div class="vue-reservation-step-3">
        <div class="background-wrapper">
            <template v-if="!isLoggedIn && !continueAsGuest">
                <h2 class="virtual-h3">{{ translations.onlyYourDataLeft }}</h2>
                <div class="row mb-5 pt-3">
                    <div class="col-md-5 col-12 mb-3 mb-sm-0">
                        <button class="btn-primary w-100" @click="handleLoginModal">
                            {{ translations.login }}
                        </button>
                    </div>
                    <div class="col-md-7 col-12">
                        <button class="btn-ghost btn-no-arrow w-100" @click="continueAsGuest = true">
                            {{ translations.continueAsGuest }}
                        </button>
                    </div>
                </div>

                <Modal ref="loginModal" name="login-modal" :title="translations.login" :width="450">
                    <LoginForm @logged-in="handleLoggedIn" />
                </Modal>
            </template>

            <template v-else>
                <h2 class="virtual-h3">{{ isLoggedIn ? translations.dataCorrect : translations.onlyYourDataLeft }}</h2>

                <FormulateInput
                    type="radio"
                    v-model="title"
                    :options="{dhr: translations.maleTitle, mevr: translations.femaleTitle, anders: translations.otherTitle }"
                    validation="required"
                    :required="true"
                    element-class="row"
                    input-class="col-auto"
                ></FormulateInput>

            <div class="row">
                <div class="col-sm-6">
                    <FormulateInput
                        type="text"
                        v-model="firstName"
                        :label="translations.firstName"
                        label-class="form-required"
                        validation="required"
                        :required="true"
                    ></FormulateInput>
                </div>

                <div class="col-sm-6">
                    <FormulateInput
                        type="text"
                        v-model="lastName"
                        :label="translations.lastName"
                        label-class="form-required"
                        validation="required"
                        :required="true"
                    ></FormulateInput>
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <FormulateInput
                        type="text"
                        v-if="isBusiness"
                        v-model="companyName"
                        :label="translations.companyName"
                        label-class="form-required"
                        validation="required"
                        :required="isBusiness"
                    ></FormulateInput>
                </div>
            </div>


            <fieldset>
                <legend>
                    {{ translations.addressData }}
                </legend>

                <FormulateInput
                    type="select"
                    v-model="country"
                    :label="translations.country"
                    label-class="form-required w-100"
                    input-class="w-100"
                    :options="{
                        'NL': 'Nederland',
                        'BE': 'België',
                        'DE': 'Duitsland',
                    }"
                    validation="required"
                    :required="true"
                ></FormulateInput>

                <div class="row">
                    <div class="col-6">
                        <FormulateInput
                            type="text"
                            v-model="postalCode"
                            :label="translations.postalCode"
                            label-class="form-required"
                            validation="required"
                            :required="true"
                        ></FormulateInput>
                    </div>

                    <div class="col-6">
                        <FormulateInput
                            type="text"
                            v-model="houseNumber"
                            :label="translations.houseNumber"
                            label-class="form-required"
                            validation="required"
                            :required="true"
                        ></FormulateInput>
                    </div>
                </div>

                <template v-if="showZipCodeValidation">
                    <div class="alert alert-warning">
                        <p class="mb-0">{{ this.translations.zipCodeValidation }}</p>
                    </div>
                </template>

                <FormulateInput
                    type="text"
                    v-model="street"
                    :label="translations.streetName"
                    label-class="form-required"
                    :element-class="zipCodeLoadingClass"
                    :disabled="zipCodeLoading"
                    validation="required"
                    :required="true"
                ></FormulateInput>

                <FormulateInput
                    type="text"
                    v-model="city"
                    :label="translations.placeName"
                    label-class="form-required"
                    :element-class="zipCodeLoadingClass"
                    :disabled="zipCodeLoading"
                    validation="required"
                    :required="true"
                ></FormulateInput>
            </fieldset>

            <fieldset>
                <legend>
                    {{ translations.contactDetails }}
                </legend>

                <FormulateInput
                    type="email"
                    v-model="email"
                    :label="translations.emailAddress"
                    label-class="form-required"
                    validation="required|email"
                    :required="true"
                ></FormulateInput>

                <template v-if="!isLoggedIn">
                    <div class="form-type-checkbox form-item">
                        <label for="create_account">{{ translations.createAccount }}</label>
                        <input
                            type="checkbox"
                            id="create_account"
                            v-model="createAccount"
                            name="create_account">
                    </div>

                    <div v-if="createAccount">
                        <div>
                            <FormulateInput
                                type="password"
                                v-model="password"
                                :label="translations.password"
                                label-class="form-required"
                                validation="required|min:8,length"
                                :required="true"
                            ></FormulateInput>
                        </div>
                        <div>
                            <FormulateInput
                                type="password"
                                v-model="passwordConfirm"
                                :label="translations.confirmPassword"
                                label-class="form-required"
                                validation="required|confirm"
                                :required="true"
                            ></FormulateInput>
                        </div>
                    </div>
                </template>

                <FormulateInput
                    type="tel"
                    v-model="phoneNumber"
                    :label="translations.phoneNumber"
                    label-class="form-required"
                    validation="required"
                    :required="true"
                ></FormulateInput>
            </fieldset>

            <div class="form-type-checkbox form-item">
                <label for="newsletter">{{ translations.registerForNewsletter }}</label>
                <input
                    type="checkbox"
                    id="newsletter"
                    v-model="newsletter"
                    name="newsletter"
                    @change="handleSetFormValue(newsletter, 'NEWSLETTER')">
            </div>

            <div class="mb-5">
                <div class="form-item">
                    <FormulateInput
                        type="checkbox"
                        v-model="termsAndConditions"
                        :label="translations.acceptTermsAndConditions"
                        label-class="form-required"
                        validation="required"
                        :required="true"
                    >
                        <template #label="{ label, id }">
                            <label :for="id">Ik heb de <a href="/over-autohopper/algemene-voorwaarden" target="_blank">algemene
                                voorwaarden</a> en de
                                <a href="/over-autohopper/privacyverklaring" target="_blank">privacyverklaring</a>
                                gelezen
                                en ga hiermee akkoord.</label>
                        </template>
                    </FormulateInput>
                </div>
            </div>

            <div class="mb-5">
                <div class="form-item">
                    <a class="d-block" href="#" @click="handleDiscountPopup">
                        <template v-if="!getActiveDiscount">{{ translations.discountLinkEmpty }}</template>
                        <template v-if="getActiveDiscount">{{ translations.discountLinkChange }}</template>
                    </a>
                </div>
            </div>


            <template v-if="hasAlert">
                <div class="alert alert-warning d-flex justify-content-between mb-5">
                    <ul class="list-style-none m-0">
                        <li v-for="message in alertMessages">{{ message }}</li>
                    </ul>

                    <button @click="alertMessages = []" class="close-button">Close</button>
                </div>
            </template>

            <p class="d-flex">
                <button @click="handleModal" class="information-button form-type-checkbox"></button>
                <span>
                    <span class="font-weight-bold d-block">{{ translations.nonBindingText }}</span>
                    {{ translations.freeCancellation }}
                </span>
            </p>

            </template>
        </div>

        <div class="button-wrapper-mobile row">
            <div class="col-12 d-flex justify-content-between">
                <router-link class="btn-ghost btn-no-arrow"
                             :to="{ name: this.isBusiness ? 'stepOneBusiness' : 'stepOne', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: getActiveVehicleWrapper.vehicle_wrapper_id } }">
                    {{ translations.backOptionsText }}
                </router-link>
            </div>
            <div class="col-12 d-flex justify-content-between">
                <router-link class="btn-primary me-0"
                             :to="{ name: this.isBusiness ? 'stepThreeBusiness' : 'stepThree', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: getActiveVehicleWrapper.vehicle_wrapper_id } }">
                    {{ translations.sendRequest }}
                </router-link>
            </div>
        </div>


        <div class="button-wrapper row">
            <div class="col-12 d-flex justify-content-between display-desktop">
                <router-link class="btn-ghost btn-no-arrow"
                             :to="{ name: this.isBusiness ? 'stepTwoBusiness' : 'stepTwo', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: getActiveVehicleWrapper.vehicle_wrapper_id } }">
                    {{ translations.backText }}
                </router-link>

                <button class="btn-primary me-0" :disabled="isDisabled" @click="handleSendRequest">
                    {{ translations.sendRequest }}
                </button>
            </div>

            <div class="col-12 d-flex justify-content-between display-mobile">
                <router-link class="btn-ghost btn-no-arrow"
                             :to="{ name: this.isBusiness ? 'stepOneBusiness' : 'stepOne', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: getActiveVehicleWrapper.vehicle_wrapper_id } }">
                    {{ translations.backText }}
                </router-link>

                <div class="d-flex mb-0 total-price-button mr-1">
                    <div class="mr-auto p-2 price-label align-middle">{{ translations.rentalPrice }}<br>
                        <a href="#receipt" class="receipt-link">{{ translations.checkReceipt }}</a></div>
                    <div class="p-2 price-total">{{ getTotalRentalPrice | vatExcluder | currencyFormatter }}</div>
                </div>

                <button class="btn-primary me-0" :disabled="isDisabled" @click="handleSendRequest">
                    {{ translations.sendRequestMobile }}
                </button>
            </div>

        </div>
    </div>
</template>

<script>
import {mapGetters, mapActions} from "vuex";
import RouterLinks from "./RouterLinks";
import translations from "../../mixins/translations";
import ModalWrapper from "./ModalWrapper";
import LoginForm from "./LoginForm";
import Modal from "./Modal";
import lodash from "lodash";
import router from '../../router';
import DiscountPopup from "./DiscountPopup.vue";
import CurrencyFormatter from "../../mixins/CurrencyFormatter";

export default {
    name: "ReservationStepThree",

    mixins: [translations, CurrencyFormatter],

    components: {
        'aut-router-links': RouterLinks,
        LoginForm,
        Modal
    },

    data() {
        return {
            isBusiness: null,
            title: '',
            companyName: '',
            newsletter: false,
            termsAndConditions: false,
            alertMessages: [],
            sessionToken: '',
            showZipCodeValidation: false,
            zipCodeLoading: false,
            vehicleId: null,
            locationId: null,
            continueAsGuest: false,
        }
    },

    computed: {
        ...mapGetters([
            'getForm',
            'getActiveVehicle',
            'getActiveLocation',
            'getActivePickupPoint',
            'getStartDate',
            'getEndDate',
            'getActiveRentalOptionIds',
            'getLoadingRentalRequest',
            'getKilometers',
            'getTotalRentalPrice',
            'getActiveRentalOptions',
            'getActiveDiscountCode',
            'getActiveDiscount',
            'getDays',
            'getTotalVehicleRates',
            'getItemsForGA4',
            'getActiveVehicleWrapper',
            'getActivePickupPointFormattedAddress',
        ]),

        hasErrors() {
            let hasErrors = false;

            const excludeKeys = ['create_account', 'password', 'password_confirm', 'newsletter'];
            
            lodash.forEach(this.getForm, (item, key) => {
                if (excludeKeys.includes(key)) {
                    return;
                }
                if (lodash.isEmpty(item) && !lodash.isBoolean(item)) {
                    hasErrors = true;
                }
            });

            if (this.createAccount) {
                if (lodash.isEmpty(this.password) || lodash.isEmpty(this.passwordConfirm) || this.password !== this.passwordConfirm || this.password.length < 8) {
                    hasErrors = true;
                }
            }

            return hasErrors || !this.termsAndConditions;
        },

        hasAlert() {
            return !lodash.isEmpty(this.alertMessages);
        },

        isDisabled() {
            if (!this.isLoggedIn && !this.continueAsGuest) {
                return true;
            }

            return this.getLoadingRentalRequest;
        },

        hasValidEmail() {
            let regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;
            return (regex.test(this.getForm.email));
        },

        hasValidZipCode() {
            let regex = /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i;
            return (regex.test(this.getForm.postal_code));
        },

        hasGenderSelected() {
            return !lodash.isEmpty(this.getForm.title)
        },

        activeVehicleIsElectric() {
            return lodash.isEqual(this.getActiveVehicle.fuel.id, 'electric');
        },

        firstName: {
            get() {
                return this.getForm.first_name;
            },
            set(value) {
                this.handleSetFormValue(value, 'FIRST_NAME');
            }
        },

        lastName: {
            get() {
                return this.getForm.last_name;
            },
            set(value) {
                this.handleSetFormValue(value, 'LAST_NAME');
            }
        },

        postalCode: {
            get() {
                return this.getForm.postal_code;
            },
            set(value) {
                this.handleSetFormValue(value, 'POSTAL_CODE');
                this.handlePostalCodeChange();
            }
        },

        houseNumber: {
            get() {
                return this.getForm.house_number;
            },
            set(value) {
                this.handleSetFormValue(value, 'HOUSE_NUMBER');
                this.handlePostalCodeChange();
            }
        },

        street: {
            get() {
                return this.getForm.street;
            },
            set(value) {
                this.handleSetFormValue(value, 'STREET');
            }
        },

        city: {
            get() {
                return this.getForm.city;
            },
            set(value) {
                this.handleSetFormValue(value, 'CITY');
            }
        },

        country: {
            get() {
                return this.getForm.country;
            },
            set(value) {
                this.handleSetFormValue(value, 'COUNTRY');
            }
        },

        email: {
            get() {
                return this.getForm.email;
            },
            set(value) {
                this.handleSetFormValue(value, 'EMAIL');
            }
        },

        phoneNumber: {
            get() {
                return this.getForm.phone_number;
            },
            set(value) {
                this.handleSetFormValue(value, 'PHONE_NUMBER');
            }
        },

        getTransactionId() {
            return Date.now();
        },

        formCountryIsNetherlands() {
            return this.getForm.country === 'NL';
        },

        isLoggedIn() {
            return this.$store.state.isLoggedIn;
        },

        createAccount: {
            get() {
                return this.getForm.create_account;
            },
            set(value) {
                this.handleSetFormValue(value, 'CREATE_ACCOUNT');
            }
        },

        password: {
            get() {
                return this.getForm.password;
            },
            set(value) {
                this.handleSetFormValue(value, 'PASSWORD');
            }
        },

        passwordConfirm: {
            get() {
                return this.getForm.password_confirm;
            },
            set(value) {
                this.handleSetFormValue(value, 'PASSWORD_CONFIRM');
            }
        },
    },

    created() {
        this.isBusiness = router.currentRoute.meta?.isBusiness ?? false;

        this.vehicleId = this.$route.query.vehicleId

        if (!lodash.isNil(this.$route.query.locationId)) {
            this.locationId = this.$route.query.locationId
        }

        this.setCurrentStep(this.$route.name);
        this.fetchSessionToken();
        this.fetchUserData();
    },

    mounted() {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            'event': 'add_shipping_info',
            'ecommerce': {
                'currency': 'EUR',
                'value': this.getTotalRentalPrice,
                'items': this.getItemsForGA4,
                'retrieve_date': this.getStartDate,
                'handin_date': this.getEndDate,
            },
        });
    },

    methods: {
        ...mapActions([
            'setCurrentStep',
            'setFormValue',
            'setLoadingRentalRequest',
            'setStoreInSessionStorage'
        ]),

        fetchUserData() {
            if (typeof drupalSettings !== 'undefined' && (drupalSettings.user?.uid ?? 0) > 0) {
                axios.get('/api/customer/mendix-user-data')
                    .then(response => {
                        const userData = response.data;

                        if (!userData) {
                            return;
                        }

                        const mapping = {
                            'Titel': 'TITLE',
                            'Voornaam': 'FIRST_NAME',
                            'Achternaam': 'LAST_NAME',
                            'Postcode': 'POSTAL_CODE',
                            'Huisnummer': 'HOUSE_NUMBER',
                            'Straat': 'STREET',
                            'Stad': 'CITY',
                            'Land': 'COUNTRY',
                            'Email': 'EMAIL',
                            'Telefoon': 'PHONE_NUMBER',
                        };

                        lodash.forEach(mapping, (formKey, userDataKey) => {
                            if (userData[userDataKey]) {
                                let value = userData[userDataKey];

                                // Normalize title if needed
                                if (userDataKey === 'Titel') {
                                    if (value === 'Dhr.') value = 'dhr';
                                    if (value === 'Mevr.') value = 'mevr';
                                }

                                this.handleSetFormValue(value, formKey);

                                // Special case for title as it has its own data property and watcher
                                if (userDataKey === 'Titel') {
                                    this.title = value;
                                }
                            }
                        });

                        this.handlePostalCodeChange();
                    })
                    .catch(error => {
                        console.error('Error fetching user data:', error);
                    });
            }
        },

        handleDiscountPopup() {
            this.$modal.show(DiscountPopup, {
                props: {
                    title: `${window.Drupal.t('Discount code')}`,
                },
                transition: 'fade',
                classes: 'vue-pop-up-discount',
            });
        },

        fetchSessionToken() {
            axios.get('/session/token')
                .then(response => {
                    this.sessionToken = response.data;
                });
        },

        handleSetFormValue(value, key) {
            this.setFormValue({
                value: value,
                key: key,
            });
        },

        handleSendRequest() {
            if (!this.hasGenderSelected) {
                this.addAlertMessage(window.Drupal.t('Please select a gender'));
            }

            if (this.hasGenderSelected) {
                this.removeAlertMessage(window.Drupal.t('Please select a gender'));
            }

            if (!this.termsAndConditions) {
                this.addAlertMessage(window.Drupal.t('Please accept the terms and conditions'));
            }

            if (this.termsAndConditions) {
                this.removeAlertMessage(window.Drupal.t('Please accept the terms and conditions'));
            }

            if (this.hasErrors) {
                this.addAlertMessage(window.Drupal.t('Please fill in all required fields'));
            }

            if (!this.hasErrors) {
                this.removeAlertMessage(window.Drupal.t('Please fill in all required fields'));
            }

            if (!this.hasValidEmail) {
                this.addAlertMessage(this.translations.emailValidation);
            }

            if (this.hasValidEmail) {
                this.removeAlertMessage(this.translations.emailValidation);
            }

            if (this.formCountryIsNetherlands && !this.hasValidZipCode) {
                this.addAlertMessage(this.translations.zipCodeValidation);
                return;
            }

            if (this.hasValidZipCode || !this.formCountryIsNetherlands) {
                this.removeAlertMessage(this.translations.zipCodeValidation);
            }

            if (!this.hasValidEmail || this.hasErrors || !this.termsAndConditions || !this.hasGenderSelected) {
                return;
            }

            this.postRentalRequest();
        },

        handleModal() {
            this.$modal.show(ModalWrapper, {
                props: {
                    description: `<p>Je verstuurt hiermee een vrijblijvende reserveringsaanvraag, we nemen binnen 24 uur contact met je op om deze door te spreken. Je kan je reservering tot 24 uur van tevoren kosteloos annuleren.</p>`,
                },
                label: `${this.translations.moreInformation} ${lodash.lowerCase(this.translations.freeCancellation)}`,
                transition: 'fade',
            });
        },

        handleLoginModal() {
            this.$refs.loginModal.show();
        },

        handleLoggedIn() {
            console.log('Handle logged in called via v-on!');
            this.$refs.loginModal.hide();
        },

        postRentalRequest() {
            this.setLoadingRentalRequest(true);

            axios.post('/api/reservation/create?_format=json', {
                ...this.getForm,
                pickup_location: this.getActivePickupPointFormattedAddress,
                company_name: this.companyName,
                start_date: this.getStartDate,
                end_date: this.getEndDate,
                rental_options: this.getActiveRentalOptionIds,
                discount_code: this.getActiveDiscountCode,
                location_id: this.getActiveLocation.id,
                pickup_point_id: this.getActivePickupPoint.id,
                vehicle_id: this.getActiveVehicle.id,
                kilometers: this.getKilometers,
                kilometers_a_day: this.getActiveVehicle.free_kilometers ?? 100,
                is_electric: this.activeVehicleIsElectric,
                is_business: this.isBusiness,
            }, {
                headers: {
                    'X-CSRF-TOKEN': this.sessionToken
                }
            })
                .then(response => {
                    if (response.data.error) {
                        this.addAlertMessage(response.data.error);
                        return;
                    }

                    if (response.data !== 'success') {
                        this.addAlertMessage(this.translations.errorTryAgainText);
                        return;
                    }

                    this.setStoreInSessionStorage().finally(() => {
                        window.dataLayer.push({ecommerce: null});
                        window.dataLayer.push({
                            'event': 'purchase',
                            'ecommerce': {
                                'currency': 'EUR',
                                'value': this.getTotalRentalPrice,
                                'items': this.getItemsForGA4,
                                'transaction_id': this.getTransactionId,
                                'retrieve_date': this.getStartDate,
                                'handin_date': this.getEndDate,
                            },
                            'customerEmail': this.email,
                            'customerPhone': this.phoneNumber,
                            'customerFirstname': this.firstName,
                            'customerLastname': this.lastName,
                            'customerStreet': this.street,
                            'customerHouseNumber': this.houseNumber,
                            'customerCity': this.city,
                            'customerCountry': this.country,
                            'customerPostalcode': this.postalCode,
                        });

                        if (this.isBusiness == true) {
                            window.location = '/reserveren/bedankt?zakelijk';
                        } else {
                            window.location = '/reserveren/bedankt';
                        }
                    });
                })
                .catch(error => {
                    this.addAlertMessage(this.translations.errorTryAgainText)
                })
                .finally(() => {
                    this.setLoadingRentalRequest(false);
                });
        },

        handlePostalCodeChange: lodash.debounce(function () {
            if (lodash.isEmpty(this.getForm.postal_code) || lodash.isEmpty(this.getForm.house_number)) {
                return;
            }

            if (!this.formCountryIsNetherlands) {
                return;
            }

            if (!this.hasValidZipCode) {
                this.showZipCodeValidation = true;
                return;
            }

            this.zipCodeLoading = true;

            axios.get(`/api/postalcode?zip=${this.getForm.postal_code}&houseNumber=${this.getForm.house_number}`)
                .then(response => {
                    if (lodash.isEmpty(response.data) || lodash.isNil(response.data.street) || lodash.isNil(response.data.city)) {
                        return;
                    }

                    this.setFormValue({
                        key: 'STREET',
                        value: response.data.street,
                    });

                    this.setFormValue({
                        key: 'CITY',
                        value: response.data.city,
                    });
                })
                .finally(() => {
                    this.showZipCodeValidation = false;
                    this.zipCodeLoading = false;
                });
        }, 350),

        addAlertMessage(message) {
            if (this.alertMessages.includes(message)) {
                return;
            }

            this.alertMessages.push(message);
        },

        removeAlertMessage(message) {
            let foundMessage = this.alertMessages.find((alert) => {
                return lodash.isEqual(alert, message)
            })

            if (lodash.isNil(foundMessage)) {
                return;
            }

            this.alertMessages.splice(this.alertMessages.indexOf(message), 1);
        },

        zipCodeLoadingClass(context, classes) {
            if (!this.zipCodeLoading) {
                return classes;
            }

            return ['loading'].concat(classes);
        }
    },

    watch: {
        title(val) {
            this.setFormValue({
                value: val,
                key: 'TITLE'
            })
        },
    }
}
</script>

<style></style>
